import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stethoscope, UserPlus, Hospital, Pencil, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_NEW = {
  email: "",
  password: "",
  name: "",
  doctor_name: "",
  specialty: "",
  license_number: "",
  ptr_number: "",
  s2_number: "",
  assignments: [], // [{hospital_id, role}]
};

const ROLES = ["doctor", "reception", "staff", "hospital_admin", "admin"];
const ROLE_LABELS = {
  doctor: "Doctor",
  reception: "Reception",
  staff: "Pharmacy / Staff",
  hospital_admin: "Hospital Admin",
  admin: "Admin",
};

function normalizeRoles(a) {
  if (Array.isArray(a?.roles) && a.roles.length) return a.roles;
  return a?.role ? [a.role] : ["doctor"];
}

function primaryRole(roles) {
  const priority = ["admin", "hospital_admin", "reception", "doctor", "staff"];
  for (const r of priority) if ((roles || []).includes(r)) return r;
  return roles?.[0] || "doctor";
}

export default function PlatformDoctors() {
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_NEW);
  const [editTarget, setEditTarget] = useState(null);
  const [editAssignments, setEditAssignments] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, h] = await Promise.all([api.get("/platform/doctors"), api.get("/platform/hospitals")]);
      setDoctors(d.data || []);
      setHospitals(h.data || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not load doctors");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleAssignment = (hospitalId) => {
    const existing = draft.assignments.find((a) => a.hospital_id === hospitalId);
    if (existing) {
      setDraft({ ...draft, assignments: draft.assignments.filter((a) => a.hospital_id !== hospitalId) });
    } else {
      setDraft({ ...draft, assignments: [...draft.assignments, { hospital_id: hospitalId, role: "doctor" }] });
    }
  };

  const setAssignmentRole = (hospitalId, role) => {
    setDraft({
      ...draft,
      assignments: draft.assignments.map((a) => a.hospital_id === hospitalId ? { ...a, role } : a),
    });
  };

  const create = async () => {
    if (!draft.email || !draft.password || !draft.name) {
      toast.error("Email, password, and name are required");
      return;
    }
    if (draft.assignments.length === 0) {
      toast.error("Select at least one hospital");
      return;
    }
    setSaving(true);
    try {
      await api.post("/platform/doctors", draft);
      toast.success("Doctor created and assigned");
      setAddOpen(false);
      setDraft(EMPTY_NEW);
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (doctor) => {
    setEditTarget(doctor);
    // Only "active" memberships are editable; "inactive" remain in DB but not shown as checked
    const active = (doctor.memberships || []).filter((m) => m.status === "active");
    setEditAssignments(active.map((m) => ({ hospital_id: m.hospital_id, role: m.role, roles: normalizeRoles(m) })));
  };

  const editToggle = (hid) => {
    const existing = editAssignments.find((a) => a.hospital_id === hid);
    if (existing) setEditAssignments(editAssignments.filter((a) => a.hospital_id !== hid));
    else setEditAssignments([...editAssignments, { hospital_id: hid, role: "doctor", roles: ["doctor"] }]);
  };

  const editToggleRole = (hid, role) => {
    setEditAssignments(editAssignments.map((a) => {
      if (a.hospital_id !== hid) return a;
      const roles = normalizeRoles(a);
      const next = roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role];
      const clean = next.length ? next : ["doctor"];
      return { ...a, roles: clean, role: primaryRole(clean) };
    }));
  };

  const saveAssignments = async () => {
    setSaving(true);
    try {
      await api.put(`/platform/doctors/${editTarget.id}/assignments`, { assignments: editAssignments });
      toast.success("Assignments updated");
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid="platform-doctors-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>
            Doctors
          </h1>
          <p className="text-slate-500 text-sm mt-1">Create doctors and assign them to one or more hospitals. Their RX profile follows them across hospitals.</p>
        </div>
        <Button onClick={() => { setDraft(EMPTY_NEW); setAddOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-add-doctor">
          <UserPlus size={14} className="mr-2" /> New Doctor
        </Button>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Specialty</th>
              <th className="text-left px-4 py-3">License #</th>
              <th className="text-left px-4 py-3">Hospitals</th>
              <th className="text-right px-4 py-3 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
            ) : doctors.length === 0 ? (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">No doctors yet. Click <b>New Doctor</b> to create one.</td></tr>
            ) : doctors.map((d) => (
              <tr key={d.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`doctor-row-${d.email}`}>
                <td className="px-4 py-3 font-mono text-xs">{d.email}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{d.doctor_name || d.name}</td>
                <td className="px-4 py-3 text-slate-600">{d.specialty || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{d.license_number || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(d.memberships || []).filter((m) => m.status === "active").map((m) => (
                      <span key={m.hospital_id} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-[11px] px-2 py-0.5 rounded">
                        <Hospital size={10} />{m.hospital_name}
                        <span className="text-indigo-500 uppercase">{normalizeRoles(m).join(" + ")}</span>
                      </span>
                    ))}
                    {(d.memberships || []).filter((m) => m.status === "active").length === 0 ? (
                      <span className="text-xs text-slate-400">No active assignments</span>
                    ) : null}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`btn-edit-assignments-${d.email}`}>
                    <Pencil size={13} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Add doctor dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="add-doctor-dialog">
          <DialogHeader><DialogTitle>Create New Doctor</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email *</Label><Input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="doctor@example.com" data-testid="new-doctor-email" /></div>
              <div><Label>Initial password *</Label><Input value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} placeholder="At least 6 chars" data-testid="new-doctor-password" /></div>
            </div>
            <div><Label>Full name *</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="DR. JOHN DOE, MD" data-testid="new-doctor-name" /></div>
            <div><Label>Display name on RX (defaults to name)</Label><Input value={draft.doctor_name} onChange={(e) => setDraft({ ...draft, doctor_name: e.target.value })} data-testid="new-doctor-display" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Specialty</Label><Input value={draft.specialty} onChange={(e) => setDraft({ ...draft, specialty: e.target.value })} data-testid="new-doctor-specialty" /></div>
              <div><Label>License #</Label><Input value={draft.license_number} onChange={(e) => setDraft({ ...draft, license_number: e.target.value })} data-testid="new-doctor-license" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>PTR #</Label><Input value={draft.ptr_number} onChange={(e) => setDraft({ ...draft, ptr_number: e.target.value })} data-testid="new-doctor-ptr" /></div>
              <div><Label>S2 #</Label><Input value={draft.s2_number} onChange={(e) => setDraft({ ...draft, s2_number: e.target.value })} data-testid="new-doctor-s2" /></div>
            </div>

            <div className="pt-3 border-t">
              <Label className="text-base font-semibold">Hospital Assignments *</Label>
              <p className="text-xs text-slate-500 mb-2">Select one or more hospitals. Choose the role per hospital.</p>
              <div className="space-y-2 max-h-64 overflow-y-auto border border-slate-200 rounded-md p-3">
                {hospitals.length === 0 ? (
                  <div className="text-sm text-slate-500">No hospitals yet. Create hospitals first.</div>
                ) : hospitals.map((h) => {
                  const assignment = draft.assignments.find((a) => a.hospital_id === h.id);
                  const checked = !!assignment;
                  return (
                    <div key={h.id} className="flex items-center gap-3" data-testid={`assign-row-${h.id}`}>
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleAssignment(h.id)}
                        data-testid={`assign-checkbox-${h.id}`}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{h.hospital_name}</div>
                        <div className="text-xs text-slate-500">{h.address}</div>
                      </div>
                      <div className="flex flex-wrap gap-1 justify-end max-w-[360px]" data-testid={`assign-role-${h.id}`}>
                        {ROLES.map((r) => {
                          const selected = normalizeRoles(assignment).includes(r);
                          return (
                            <button
                              type="button"
                              key={r}
                              disabled={!checked}
                              onClick={() => setAssignmentRole(h.id, r)}
                              className={`text-[10px] px-2 py-1 rounded border ${
                                selected ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200"
                              } disabled:opacity-40`}
                            >
                              {ROLE_LABELS[r] || r}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-create-doctor">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Create Doctor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit assignments dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="edit-assignments-dialog">
          <DialogHeader><DialogTitle>Hospital Assignments — {editTarget?.doctor_name || editTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto border border-slate-200 rounded-md p-3">
            {hospitals.map((h) => {
              const assignment = editAssignments.find((a) => a.hospital_id === h.id);
              const checked = !!assignment;
              return (
                <div key={h.id} className="flex items-center gap-3" data-testid={`edit-assign-row-${h.id}`}>
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => editToggle(h.id)}
                    data-testid={`edit-assign-checkbox-${h.id}`}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{h.hospital_name}</div>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end max-w-[360px]" data-testid={`edit-assign-role-${h.id}`}>
                    {ROLES.map((r) => {
                      const selected = normalizeRoles(assignment).includes(r);
                      return (
                        <button
                          type="button"
                          key={r}
                          disabled={!checked}
                          onClick={() => editToggleRole(h.id, r)}
                          className={`text-[10px] px-2 py-1 rounded border ${
                            selected ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200"
                          } disabled:opacity-40`}
                        >
                          {ROLE_LABELS[r] || r}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={saveAssignments} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-save-assignments">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
